import { useEffect, useState } from 'react';
import { compradorTrpc } from '../../lib/compradorTrpc';
import { AppShellComprador } from '../../components/comprador/AppShellComprador';
import { limparSessaoComprador } from '../../lib/compradorAuth';
import { useCompradorSessao, rotaApp } from './util';

/**
 * Perfil do comprador — onde ele vê e edita os próprios dados e preferências:
 * nome e telefone (o e-mail é a identidade de login, read-only), notificação de
 * avanço da obra, e a opção de CRIAR/TROCAR SENHA (migrada de cá, antes solta no
 * topo). O "Sair" fica aqui (e também no rodapé do drawer).
 */
export function PerfilComprador() {
  const slug = useCompradorSessao();
  const [me, setMe] = useState<{ orgNome: string; corMarca: string | null } | null>(null);
  const [perfil, setPerfil] = useState<{ nome: string; email: string | null; telefone: string | null; notificarObra: boolean } | null>(null);
  const [temSenha, setTemSenha] = useState<boolean | null>(null);

  // Edição de dados
  const [nome, setNome] = useState('');
  const [telefone, setTelefone] = useState('');
  const [salvandoDados, setSalvandoDados] = useState(false);
  const [dadosOk, setDadosOk] = useState(false);
  const [dadosErro, setDadosErro] = useState<string | null>(null);

  // Notificação (otimista)
  const [notificar, setNotificar] = useState(true);

  // Senha
  const [senha, setSenha] = useState('');
  const [confirma, setConfirma] = useState('');
  const [salvandoSenha, setSalvandoSenha] = useState(false);
  const [senhaOk, setSenhaOk] = useState(false);
  const [senhaErro, setSenhaErro] = useState<string | null>(null);

  useEffect(() => {
    if (!slug) return;
    compradorTrpc.auth.me.query({ orgSlug: slug }).then((m) => setMe({ orgNome: m.orgNome, corMarca: m.corMarca })).catch(() => {});
    compradorTrpc.app.perfil.query({ orgSlug: slug }).then((p) => {
      setPerfil(p);
      setNome(p.nome);
      setTelefone(p.telefone ?? '');
      setNotificar(p.notificarObra);
    }).catch(() => {});
    compradorTrpc.auth.temSenha.query({ orgSlug: slug }).then((r) => setTemSenha(r.temSenha)).catch(() => {});
  }, [slug]);

  async function salvarDados() {
    if (!slug) return;
    setDadosErro(null);
    setDadosOk(false);
    if (nome.trim().length < 1) return setDadosErro('Informe seu nome.');
    setSalvandoDados(true);
    try {
      await compradorTrpc.app.atualizarPerfil.mutate({ orgSlug: slug, nome: nome.trim(), telefone: telefone.trim() || null });
      setDadosOk(true);
    } catch (e) {
      setDadosErro((e as Error).message || 'Não foi possível salvar.');
    } finally {
      setSalvandoDados(false);
    }
  }

  async function alternarNotificar(valor: boolean) {
    if (!slug) return;
    setNotificar(valor); // otimista
    try {
      await compradorTrpc.app.setNotificarObra.mutate({ orgSlug: slug, valor });
    } catch {
      setNotificar(!valor); // desfaz
    }
  }

  async function salvarSenha() {
    if (!slug) return;
    setSenhaErro(null);
    setSenhaOk(false);
    if (senha.length < 8) return setSenhaErro('A senha precisa ter pelo menos 8 caracteres.');
    if (senha !== confirma) return setSenhaErro('As duas senhas não são iguais.');
    setSalvandoSenha(true);
    try {
      await compradorTrpc.auth.definirSenha.mutate({ orgSlug: slug, senha });
      setSenhaOk(true);
      setTemSenha(true);
      setSenha('');
      setConfirma('');
    } catch (e) {
      setSenhaErro((e as Error).message || 'Não foi possível salvar a senha.');
    } finally {
      setSalvandoSenha(false);
    }
  }

  function sair() {
    if (!slug) return;
    limparSessaoComprador(slug);
    window.location.assign(rotaApp(slug));
  }

  const input = 'h-[46px] w-full rounded-xl border border-line-2 bg-surface px-[15px] text-sm text-ink outline-none focus:border-accent';
  const marca = { background: 'var(--brand, var(--accent))' } as const;

  return (
    <AppShellComprador corMarca={me?.corMarca} orgNome={me?.orgNome}>
      <div className="mx-auto max-w-md" data-testid="perfil-comprador">
        <h1 className="font-serif text-[26px] font-medium text-ink">Perfil</h1>
        <p className="mt-1 text-sm text-ink-3">Seus dados e preferências no app da {me?.orgNome ?? 'incorporadora'}.</p>

        {/* Seus dados */}
        <section className="mt-5 rounded-2xl border border-line bg-surface p-4">
          <div className="text-[13px] font-semibold text-ink">Seus dados</div>
          <div className="mt-3 space-y-3">
            <div>
              <label className="mb-1.5 block text-[12.5px] font-medium text-ink-2" htmlFor="perfil-nome">Nome</label>
              <input id="perfil-nome" value={nome} onChange={(e) => setNome(e.target.value)} className={input} maxLength={160} data-testid="perfil-nome" />
            </div>
            <div>
              <label className="mb-1.5 block text-[12.5px] font-medium text-ink-2" htmlFor="perfil-email">E-mail</label>
              <input id="perfil-email" value={perfil?.email ?? ''} readOnly disabled className={`${input} opacity-70`} data-testid="perfil-email" />
              <p className="mt-1 text-[11.5px] text-ink-4">É por onde você entra no app. Para trocar, fale com a {me?.orgNome ?? 'incorporadora'}.</p>
            </div>
            <div>
              <label className="mb-1.5 block text-[12.5px] font-medium text-ink-2" htmlFor="perfil-telefone">Telefone</label>
              <input id="perfil-telefone" value={telefone} onChange={(e) => setTelefone(e.target.value)} placeholder="(00) 00000-0000" className={input} maxLength={40} data-testid="perfil-telefone" inputMode="tel" />
            </div>
          </div>
          {dadosErro && <p className="mt-2 text-[12px]" style={{ color: 'var(--risk)' }}>{dadosErro}</p>}
          {dadosOk && <p className="mt-2 text-[12px]" style={{ color: 'var(--accent)' }} data-testid="perfil-dados-ok">Dados salvos.</p>}
          <button type="button" onClick={salvarDados} disabled={salvandoDados} data-testid="perfil-salvar-dados" className="mt-3 w-full rounded-xl py-3 text-sm font-semibold text-white disabled:opacity-60" style={marca}>
            {salvandoDados ? 'Salvando…' : 'Salvar dados'}
          </button>
        </section>

        {/* Notificações */}
        <section className="mt-3 rounded-2xl border border-line bg-surface p-4">
          <div className="text-[13px] font-semibold text-ink">Notificações</div>
          <label className="mt-3 flex items-center justify-between gap-3">
            <span className="min-w-0">
              <span className="block text-sm font-medium text-ink">Avisar a cada avanço da obra</span>
              <span className="block text-[12px] text-ink-3">Um e-mail quando uma etapa é concluída.</span>
            </span>
            <button
              type="button"
              role="switch"
              aria-checked={notificar}
              onClick={() => alternarNotificar(!notificar)}
              data-testid="perfil-notificar"
              className="relative h-6 w-11 shrink-0 rounded-full transition-colors"
              style={{ background: notificar ? 'var(--brand, var(--accent))' : 'var(--line-2)' }}
            >
              <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-transform ${notificar ? 'translate-x-[22px]' : 'translate-x-0.5'}`} />
            </button>
          </label>
        </section>

        {/* Senha */}
        <section className="mt-3 rounded-2xl border border-line bg-surface p-4" data-testid="perfil-senha">
          <div className="text-[13px] font-semibold text-ink">{temSenha ? 'Trocar senha' : 'Criar uma senha'}</div>
          <p className="mt-1 text-[12px] text-ink-3">
            Opcional. Com uma senha você entra mais rápido, sem esperar o link no e-mail. O link continua funcionando sempre.
          </p>
          {senhaOk ? (
            <p className="mt-3 text-[13px] font-medium" style={{ color: 'var(--accent)' }} data-testid="perfil-senha-ok">Senha salva.</p>
          ) : (
            <div className="mt-3 space-y-3">
              <input type="password" autoComplete="new-password" value={senha} onChange={(e) => setSenha(e.target.value)} placeholder="nova senha (mín. 8 caracteres)" className={input} data-testid="perfil-nova-senha" />
              <input type="password" autoComplete="new-password" value={confirma} onChange={(e) => setConfirma(e.target.value)} placeholder="repita a senha" className={input} data-testid="perfil-confirma-senha" />
              {senhaErro && <p className="text-[12px]" style={{ color: 'var(--risk)' }}>{senhaErro}</p>}
              <button type="button" onClick={salvarSenha} disabled={salvandoSenha} data-testid="perfil-salvar-senha" className="w-full rounded-xl border border-line-2 py-3 text-sm font-semibold text-ink disabled:opacity-60">
                {salvandoSenha ? 'Salvando…' : temSenha ? 'Trocar senha' : 'Criar senha'}
              </button>
            </div>
          )}
        </section>

        {/* Sair */}
        <button type="button" onClick={sair} data-testid="perfil-sair" className="mt-4 flex w-full items-center justify-center gap-2 rounded-2xl border border-line-2 py-3.5 text-sm font-semibold text-ink transition-colors hover:bg-surface">
          <svg viewBox="0 0 24 24" width="17" height="17" fill="none" aria-hidden="true">
            <path d="M15 12H4m0 0l3.5-3.5M4 12l3.5 3.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M11 6V5a2 2 0 012-2h4a2 2 0 012 2v14a2 2 0 01-2 2h-4a2 2 0 01-2-2v-1" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          Sair
        </button>
      </div>
    </AppShellComprador>
  );
}
